import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { Send20Regular } from "@fluentui/react-icons";
import { ToolCalled } from '../../Types/Chat/ToolCalled';
import { ChatProgress } from '../../Types/Chat/ChatProgress';
import { ChatBubble } from './ChatBubble';
import { getAnimatedText } from './AnimatedText';
import { userId } from "../../App";
import { getAssistantGreetings, getChatHistoryBySession, getChatProgress, getPendingQueryId, postChatMessage } from '../../services/AssistantService';
import { v4 as uuidv4 } from 'uuid';
import { Spinner } from '@fluentui/react-components';

interface ChatMessage {
    id: string;
    role: string;
    message: string;
    dataSources?: { name: string, data: string, url: string | undefined }[];
    elements: ReactNode[];
    canBeRated: boolean;
    sessionId: number;
    queryId?: string | null;
    progress?: ChatProgress;
}

interface AssistantChatProps {
    sessionId?: number;
    initialPrompt?: string;
}

export function AssistantChatSide(props: AssistantChatProps) {
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingNewHistory, setIsLoadingNewHistory] = useState(false);
    const [chatHistory, setChatHistory] = useState([] as ChatMessage[]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const requestControllerRef = useRef<AbortController | null>(null);
    const activeRequestIdRef = useRef<string | null>(null);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView();
        }, 0);
    };

    // Throttled version to prevent too frequent scrolling during animation
    const scrollToBottomThrottled = React.useCallback(() => {
        messagesEndRef.current?.scrollIntoView();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [chatHistory]);

    useEffect(() => {
        (async () => {
            while (!userId || userId === "") {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            setIsLoadingNewHistory(true);
            var historyResponse = await getChatHistoryBySession(userId, props.sessionId ?? 0);
            const updatedChatHistory: ChatMessage[] = [];
            historyResponse.forEach((response) => {
                let ds: { name: string, data: string, url: string | undefined }[] = [];
                response?.toolsCalled.forEach((tool: ToolCalled) => {
                    for (let key in tool.toolArguments) {
                        ds.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                        console.log(tool.toolArguments[key]);
                    }
                });
                updatedChatHistory.push({
                    role: response.role,
                    message: response.message,
                    elements: response.elements,
                    id: response.id,
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId,
                    queryId: response.queryId,
                    dataSources: Object.keys(ds).length > 0 ? ds : undefined
                });
            });

            if (updatedChatHistory.length > 0) {
                const pendingQueryId = getPendingQueryId(historyResponse);
                if (!pendingQueryId) {
                    setChatHistory(updatedChatHistory);
                    setIsLoadingNewHistory(false);
                    return;
                }

                const queryId = pendingQueryId;
                const placeholderId = `progress-${queryId}`;
                const controller = new AbortController();
                requestControllerRef.current?.abort();
                requestControllerRef.current = controller;
                activeRequestIdRef.current = queryId;
                setIsLoading(true);
                setChatHistory([...updatedChatHistory, {
                    role: "assistant",
                    message: "showSpinner",
                    elements: [getAnimatedText("Thinking...", true, undefined, scrollToBottomThrottled)],
                    id: placeholderId,
                    canBeRated: false,
                    sessionId: -1
                }]);
                setIsLoadingNewHistory(false);

                try {
                    const response = await getChatProgress(userId, props.sessionId ?? 0, queryId, (progress) => {
                        if (activeRequestIdRef.current !== queryId) return;
                        setChatHistory((prevHistory) => prevHistory.map((chat) =>
                            chat.id === placeholderId ? { ...chat, progress } : chat
                        ));
                    }, controller.signal);
                    if (activeRequestIdRef.current !== queryId) return;

                    const dataSources: { name: string, data: string, url: string | undefined }[] = [];
                    response.toolsCalled.forEach((tool: ToolCalled) => {
                        for (let key in tool.toolArguments) {
                            dataSources.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                        }
                    });
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? {
                            id: response.id,
                            role: "assistant",
                            message: response.message,
                            dataSources: dataSources.length > 0 ? dataSources : undefined,
                            elements: response.id === "secret message"
                                ? [getAnimatedText(response.message, true, "text-sm/[6px] font-mono", scrollToBottomThrottled)]
                                : [getAnimatedText(response.message, true, undefined, scrollToBottomThrottled)],
                            canBeRated: response.canBeRated,
                            sessionId: response.sessionId,
                        } : chat
                    ));
                } catch (error) {
                    if (activeRequestIdRef.current !== queryId || (error as { name?: string })?.name === "AbortError") return;
                    const errorMessage = error instanceof Error ? error.message : "I'm sorry, I'm currently unavailable. Please try again later.";
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? {
                            ...chat,
                            id: `error-${queryId}`,
                            message: errorMessage,
                            elements: [getAnimatedText(errorMessage, false)],
                            progress: undefined,
                        } : chat
                    ));
                } finally {
                    if (activeRequestIdRef.current === queryId) {
                        activeRequestIdRef.current = null;
                        requestControllerRef.current = null;
                        setIsLoading(false);
                    }
                }
            } else if (!props.initialPrompt) {
                // Only load greeting if there's no initial prompt
                const response = await getAssistantGreetings();
                setChatHistory([{
                    role: "assistant",
                    message: response.message,
                    elements: response.elements,
                    id: response.id,
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId
                }]);
                setIsLoadingNewHistory(false);
            } else {
                // If there's an initial prompt, start with empty chat history
                setChatHistory([]);
                setIsLoadingNewHistory(false);
            }
        })();
    }, [props.sessionId]);

    useEffect(() => {
        setIsLoading(false);
        return () => {
            activeRequestIdRef.current = null;
            requestControllerRef.current?.abort();
            requestControllerRef.current = null;
        };
    }, [props.sessionId]);

    // Handle initial prompt
    useEffect(() => {
        if (props.initialPrompt && props.sessionId && props.sessionId !== -1 && !isLoadingNewHistory) {
            // Auto-submit the initial prompt after a short delay without setting it in the input field
            const timer = setTimeout(() => {
                if (props.initialPrompt) {
                    handleSubmitWithMessage(props.initialPrompt);
                }
            }, 500); // Reduced delay since we're not waiting for greeting

            return () => clearTimeout(timer);
        }
    }, [props.initialPrompt, props.sessionId, isLoadingNewHistory]);

    const handleSubmitWithMessage = async (messageToSend?: string) => {
        const messageText = messageToSend || message;
        if (!messageText.trim() || isLoading || props.sessionId === undefined) return;

        requestControllerRef.current?.abort();
        const controller = new AbortController();
        const requestId = uuidv4();
        const placeholderId = `progress-${requestId}`;
        requestControllerRef.current = controller;
        activeRequestIdRef.current = requestId;

        setIsLoading(true);
        var newHistory = [...chatHistory, { role: "user", message: messageText, elements: [getAnimatedText(messageText, false)], id: uuidv4(), canBeRated: false, sessionId: -1 }];
        setChatHistory(newHistory);
        // Always clear the message input, regardless of how this function was called
        setMessage('');
        setChatHistory([...newHistory, { role: "assistant", message: "showSpinner", elements: [getAnimatedText("Thinking...", true, undefined, scrollToBottomThrottled)], id: placeholderId, canBeRated: false, sessionId: -1 }]);

        try {
            const response = await postChatMessage(
                userId,
                props.sessionId,
                messageText,
                (progress) => {
                    if (activeRequestIdRef.current !== requestId) return;
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? { ...chat, progress } : chat
                    ));
                },
                controller.signal
            );
            if (activeRequestIdRef.current !== requestId) return;

            const dataSources: { name: string, data: string, url: string | undefined }[] = [];
            response.toolsCalled.forEach((tool: ToolCalled) => {
                for (let key in tool.toolArguments) {
                    dataSources.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                }
            });

            const responseMessage: ChatMessage = {
                id: response.id,
                role: "assistant",
                message: response.message,
                dataSources: dataSources.length > 0 ? dataSources : undefined,
                elements: response.id === "secret message"
                    ? [getAnimatedText(response.message, true, "text-sm/[6px] font-mono", scrollToBottomThrottled)]
                    : [getAnimatedText(response.message, true, undefined, scrollToBottomThrottled)],
                canBeRated: response.canBeRated,
                sessionId: response.sessionId,
            };
            setChatHistory((prevHistory) => prevHistory.map((chat) =>
                chat.id === placeholderId ? responseMessage : chat
            ));
        } catch (error) {
            if (activeRequestIdRef.current !== requestId || (error as { name?: string })?.name === "AbortError") return;

            const errorMessage = error instanceof Error
                ? error.message
                : "I'm sorry, I'm currently unavailable. Please try again later.";
            setChatHistory((prevHistory) => prevHistory.map((chat) =>
                chat.id === placeholderId
                    ? {
                        ...chat,
                        id: `error-${requestId}`,
                        message: errorMessage,
                        elements: [getAnimatedText(errorMessage, false)],
                        progress: undefined,
                    }
                    : chat
            ));
        } finally {
            if (activeRequestIdRef.current === requestId) {
                activeRequestIdRef.current = null;
                requestControllerRef.current = null;
                setIsLoading(false);
            }
        }
    };

    const handleSubmit = async () => {
        await handleSubmitWithMessage();
    };

    const setCanBeRated = (id: string, canBeRated: boolean) => {
        setChatHistory((prevHistory) =>
            prevHistory.map((chat) =>
                chat.id === id ? { ...chat, canBeRated } : chat
            )
        );
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };

    return (
        <div className="w-full h-full flex flex-col ml-6">
            {isLoadingNewHistory === false ? (
                <div className="overflow-y-auto flex-grow flex flex-col gap-4 p-4 pb-20 min-h-0">
                    {
                        chatHistory.map((chat, index) => (
                            <div
                                className={`max-w-[85%] w-fit ${chat.role === "assistant" ? "self-start" : "self-end"} rounded-xl p-3 mb-2`}
                                key={chat.id || index}
                            >
                                <ChatBubble
                                    id={chat.id}
                                    message={chat.message}
                                    role={chat.role}
                                    dataSources={chat.dataSources}
                                    animate={chat.role === "assistant" && index === chatHistory.length - 1}
                                    elements={chat.elements}
                                    canBeRated={chat.canBeRated}
                                    sessionId={chat.sessionId}
                                    setCanBeRated={setCanBeRated}
                                    progress={chat.progress}
                                />
                            </div>
                        ))
                    }
                    <div ref={messagesEndRef} />
                </div>
            ) : (
                <Spinner className="absolute place-self-center h-full" size="extra-large" />
            )}

            {/* Input area - positioned at bottom of container */}
            <div className="bg-primary-yellow p-4 text-text-light dark:text-text-dark flex-shrink-0">
                <div className="flex justify-center items-end relative w-full">
                    <input
                        type="text"
                        placeholder="Ask me anything"
                        value={message}
                        onChange={handleInputChange}
                        onKeyDown={(e) => e.key === 'Enter' && message.trim() !== '' && !isLoading && handleSubmit()}
                        className="w-[100%] text-primary-dark dark:text-text-dark text-sm p-2.5 pr-12 shadow-lg rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    <div className="absolute right-4 bottom-[10px]">
                        {isLoading ? (
                            <p className="text-sm opacity-70">...</p>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={message.trim() === ''}
                                className="hover:bg-primary-dark/10 dark:hover:bg-primary-light/10 p-1 rounded-full hover:scale-110 transition-transform duration-200 relative top-1 right-1"
                            >
                                <Send20Regular />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
}